import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, Type, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { DynamicIoModule } from 'ng-dynamic-component';

import { SDKMessageModule } from 'sdk-message';

export interface SDKTab {
    title: string; // Name on the tab.
    type?: Type<any>; // The component to display when the tab is selected.
    inputs?: any; // Input properties for the component displayed.
    outputs?: any // Output properties for the component displayed.
    urlParams?: string // Parameters to be used when sharing the tab.
}

@Component({
    selector: 'sdk-tabs',
    standalone: true,
    imports: [
        CommonModule,
        ClipboardModule,
        DynamicIoModule,
        SDKMessageModule,
    ],
    templateUrl: './sdk-tabs.component.html',
    styleUrls: ['./sdk-tabs.component.scss']
})

export class SDKTabsComponent {
    /**************************************************************************
    * Input/Output Parameters
    **************************************************************************/
    @Input() tabs: SDKTab[] = []; // List of tabs.
    @Input() activeTab: SDKTab | undefined; // Set the current active tab. This is used to load a tab from a share.
    @Input() fontFamily: string = ""; // Font to use for the tab titles.
    @Input() fontColor: string = ""; // Color to use for the tab titles.
    @Input() tabColor: string = ""; // Color of the tabs.
    @Input() tabBorderColor: string = ""; // Border color of the tabs.
    @Input() activeTabColor: string = ""; // Active tab color.
    @Input() activeFontColor: string = ""; // Active tab font color.
    @Input() height: string = ""; // Height of tabs. Default is auto.
    @Input() share: boolean = false; // Enable share icon.
    @Output() tabChangedEvent: EventEmitter<{ from: any, to: any }> = new EventEmitter(); // Callback when active tab changes.

    /**************************************************************************
    * Component Variables
    **************************************************************************/
    @ViewChild('sdktabs') sdktabs!: ElementRef<any>;
    @ViewChild('sdktabinfo') sdktabinfo!: ElementRef<any>;

    protected sharedUrl: string = "";
    protected message: string = "";

    constructor(
        private route: ActivatedRoute,
    ) {
    }

    //******************************************************************************
    //  Component Life-cycle Methods
    //******************************************************************************
    protected ngOnInit() {
        this.route.queryParams.subscribe((params: any) => {
            if (params?.tab) {
                this.activeTab = this.tabs.find((tab: SDKTab) => tab.title === params.tab);
            }
        });
    }

    protected ngOnChanges(_args: any) {
        if (_args.activeTab) {
            if (_args.activeTab.firstChange && !_args.activeTab.currentValue) {
                this.activeTab = _args.tabs.currentValue[0];
            }

            if (this.activeTab) {
                this.setSharedUrl(this.activeTab);
            }
        }

        if (_args.tabs) {
            this.setSharedUrl(this.activeTab!);
        }
    }

    protected ngAfterViewInit() {
        setTimeout(() => {
            if (this.sdktabs) {
                this.sdktabs.nativeElement.style.setProperty("--sdk-tabs-font-family", this.fontFamily);
                this.sdktabs.nativeElement.style.setProperty("--sdk-tabs-font-color", this.fontColor);
                this.sdktabs.nativeElement.style.setProperty("--sdk-tabs-color", this.tabColor);
                this.sdktabs.nativeElement.style.setProperty("--sdk-tabs-border-color", this.tabBorderColor);
                this.sdktabs.nativeElement.style.setProperty("--sdk-tabs-active-color", this.activeTabColor);
                this.sdktabs.nativeElement.style.setProperty("--sdk-tabs-active-font-color", this.activeFontColor);
                this.sdktabs.nativeElement.style.setProperty("--sdk-tabs-height", this.height);
            }
        }, 1);
    }

    //*************************************************************************
    //  Protected Methods
    //*************************************************************************
    protected setActiveTab(tab: any) {
        if (tab != this.activeTab && this.tabChangedEvent.observed) {
            this.tabChangedEvent.emit({ from: this.activeTab, to: tab });
        } else {
            this.activeTab = tab;

            this.setSharedUrl(tab);
        }

        setTimeout(() => {
            if (this.sdktabinfo) {
                this.sdktabinfo.nativeElement.scrollTop = 0;
            }
        }, 10);
    }

    protected copied() {
        this.message = "Link has been Copied!";
    }

    //*************************************************************************
    //  Private Methods
    //*************************************************************************
    private setSharedUrl(tab: SDKTab) {
        setTimeout(() => {
            this.sharedUrl = `${window.location.href.split('?')[0]}?tab=${tab?.title}`;

            if (tab && tab?.urlParams && tab?.urlParams !== "") {
                this.sharedUrl += (tab.urlParams?.startsWith("&")) ? tab.urlParams : `&${tab.urlParams}`;
            }
        }, 100);
    }
}
